import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getLeadTimezones, type LeadTimezone } from './crmApi'
import { timeFromOffset, tzOffsetMin, viewerTz } from './localTime'
import { toggleTzFilter, useTzFilter } from './tzFilter'

/**
 * WALL-CLOCK STRIP (Lee, 23 Sep 2026) — a row of little clocks under the top
 * banner, on every CRM page. One clock per DISTINCT CURRENT TIME: zones that
 * overlap right now (America/New_York + America/Detroit…) collapse into one
 * clock, labelled by the biggest zone in the group with "+N zones" for the
 * rest. Analog face + digital HH:MM, plus how many leads live in that time.
 *
 * Cheap by design: the zone list refreshes every 2 hours (server also caches
 * 2h); each zone's UTC offset is read from Intl once per 2h (localTime.ts
 * cache); between refreshes the clocks run on plain arithmetic, repainted
 * once a minute — one strip-level state tick, a handful of tiny SVGs.
 */

const cityOf = (tz: string) => (tz.split('/').pop() || tz).replace(/_/g, ' ')

// TZ ABBREVIATION (Lee, 2 Oct 2026): clocks are labelled GMT / EST / BST…
// instead of city names (cities stay in the hover tooltip). Intl gives the
// correct abbreviation for the CURRENT date (so EST↔EDT, GMT↔BST flip on DST
// boundaries); zones without a lettered abbreviation come back as GMT+5:30
// style. Cached per zone per hour.
const abbrevCache = new Map<string, { v: string; at: number }>()
const abbrevOf = (tz: string): string => {
  const hit = abbrevCache.get(tz)
  if (hit && Date.now() - hit.at < 3600_000) return hit.v
  let v = tz
  try {
    v = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value || tz
  } catch { /* unknown zone — fall back to the raw name */ }
  abbrevCache.set(tz, { v, at: Date.now() })
  return v
}

// IANA zone → country, for the flag on each clock (covers every zone the
// server's resolver can produce; unknown zones get a plain globe).
const TZ_CC: Record<string, string> = {
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Phoenix': 'US',
  'America/Los_Angeles': 'US', 'America/Anchorage': 'US', 'Pacific/Honolulu': 'US', 'America/Puerto_Rico': 'US',
  'America/Detroit': 'US', 'America/Indiana/Indianapolis': 'US', 'America/Boise': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA', 'America/Winnipeg': 'CA',
  'America/Regina': 'CA', 'America/Halifax': 'CA', 'America/St_Johns': 'CA', 'America/Whitehorse': 'CA',
  'America/Yellowknife': 'CA', 'America/Iqaluit': 'CA', 'America/Mexico_City': 'MX',
  'Europe/London': 'GB', 'Europe/Dublin': 'IE', 'Europe/Vienna': 'AT', 'Europe/Brussels': 'BE',
  'Europe/Zurich': 'CH', 'Europe/Berlin': 'DE', 'Europe/Copenhagen': 'DK', 'Europe/Madrid': 'ES',
  'Europe/Helsinki': 'FI', 'Europe/Paris': 'FR', 'Europe/Athens': 'GR', 'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL', 'Europe/Oslo': 'NO', 'Europe/Warsaw': 'PL', 'Europe/Lisbon': 'PT',
  'Europe/Stockholm': 'SE', 'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Hong_Kong': 'HK',
  'Asia/Kolkata': 'IN', 'Asia/Singapore': 'SG', 'Africa/Johannesburg': 'ZA',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
  'Australia/Adelaide': 'AU', 'Australia/Perth': 'AU', 'Australia/Hobart': 'AU', 'Australia/Darwin': 'AU',
  'Pacific/Auckland': 'NZ',
}
const flagOf = (tz: string): string => {
  const cc = TZ_CC[tz]
  if (!cc) return '🌐'
  // Country code → regional-indicator flag emoji.
  return String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65))
}

const ClockFace: React.FC<{ minutesOfDay: number; highlight: boolean }> = ({ minutesOfDay, highlight }) => {
  const h = ((minutesOfDay / 60) % 12) * 30 // hour hand degrees
  const m = (minutesOfDay % 60) * 6 // minute hand degrees
  const stroke = highlight ? '#2563EB' : 'currentColor'
  return (
    <svg viewBox="0 0 40 40" className="w-12 h-12 shrink-0">
      <circle cx="20" cy="20" r="18" fill="var(--clock-face, #fff)" stroke={stroke} strokeWidth="2" opacity="0.9" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * 30 * Math.PI) / 180
        return <line key={i} x1={20 + 15 * Math.sin(a)} y1={20 - 15 * Math.cos(a)} x2={20 + 16.5 * Math.sin(a)} y2={20 - 16.5 * Math.cos(a)} stroke={stroke} strokeWidth="1" opacity="0.5" />
      })}
      <line x1="20" y1="20" x2={20 + 8 * Math.sin((h * Math.PI) / 180)} y2={20 - 8 * Math.cos((h * Math.PI) / 180)} stroke={stroke} strokeWidth="2.4" strokeLinecap="round" />
      <line x1="20" y1="20" x2={20 + 12.5 * Math.sin((m * Math.PI) / 180)} y2={20 - 12.5 * Math.cos((m * Math.PI) / 180)} stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="20" cy="20" r="1.4" fill={stroke} />
    </svg>
  )
}

export const CrmClockStrip: React.FC = () => {
  const location = useLocation()
  const [zones, setZones] = useState<LeadTimezone[]>([])
  const [, setTick] = useState(0)
  const tzFilter = useTzFilter()
  // Collapsed state survives reloads (Lee, 14 Oct 2026); open by default.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('wq_clock_strip_collapsed') === '1' } catch { return false }
  })
  const setCollapsedPersist = (v: boolean) => {
    setCollapsed(v)
    try { localStorage.setItem('wq_clock_strip_collapsed', v ? '1' : '0') } catch { /* ignore */ }
  }

  // Zone list: on mount + every 2 hours (matches the server cache).
  useEffect(() => {
    let alive = true
    const load = () => getLeadTimezones().then((z) => { if (alive) setZones(z) }).catch(() => {})
    load()
    const t = setInterval(load, 2 * 3600_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  // One minute-aligned repaint for the whole strip.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>
    const loop = () => { setTick((n) => n + 1); t = setTimeout(loop, 60_000 - (Date.now() % 60_000) + 60) }
    t = setTimeout(loop, 60_000 - (Date.now() % 60_000) + 60)
    return () => clearTimeout(t)
  }, [])

  const myOff = tzOffsetMin(viewerTz())
  // GROUP BY CURRENT OFFSET — overlapping zones show ONE clock. Recomputed
  // each minute-tick render; offsets come from the 2h cache, so this is a
  // cheap in-memory pass.
  const groups = useMemo(() => {
    const g = new Map<number, { off: number; zones: LeadTimezone[]; count: number }>()
    for (const z of zones) {
      const off = tzOffsetMin(z.tz)
      const e = g.get(off) || { off, zones: [], count: 0 }
      e.zones.push(z)
      e.count += z.count
      g.set(off, e)
    }
    return [...g.values()].sort((a, b) => a.off - b.off)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones, Math.floor(Date.now() / 60_000)])

  // CRM pages only — and NOT inside a lead (Lee, 28 Sep 2026: the lead page
  // has its own timezone line in the Details rail; the wall just eats height).
  if (!location.pathname.startsWith('/crm') || location.pathname.startsWith('/crm/lead/') || groups.length === 0) return null

  const now = new Date()
  // LONDON IS STATIC (Lee, 23 Sep 2026): pinned far left, outside the scroll,
  // as the home reference. Its offset group is pulled out of the scrolling
  // list so it never appears twice.
  const londonOff = tzOffsetMin('Europe/London')
  const londonGroup = groups.find((g) => g.off === londonOff)
  const restGroups = groups.filter((g) => g.off !== londonOff)

  const renderClock = (gr: { off: number; zones: LeadTimezone[]; count: number }, forceTz?: string) => {
    const mine = gr.off === myOff
    const selected = tzFilter?.off === gr.off
    // Label = the group's biggest zone; the rest collapse into "+N".
    const main = forceTz ? { tz: forceTz, count: gr.count } : [...gr.zones].sort((a, b) => b.count - a.count)[0]
    const others = forceTz ? gr.zones.filter((z) => z.tz !== forceTz).length : gr.zones.length - 1
    const t = new Date(now.getTime() + gr.off * 60000)
    const minutesOfDay = t.getUTCHours() * 60 + t.getUTCMinutes()
    const clickable = gr.count > 0
    return (
      <button
        key={gr.off}
        onClick={() => clickable && toggleTzFilter({ off: gr.off, zones: gr.zones.map((z) => z.tz), label: abbrevOf(main.tz) + (others > 0 ? ` +${others}` : '') })}
        className={`flex items-center gap-2.5 shrink-0 leading-tight rounded-lg px-2.5 py-1 border transition-colors text-left ${
          selected
            ? 'bg-brand-50 border-brand-400 ring-1 ring-brand-300'
            : clickable
              ? 'border-transparent hover:bg-surface-hover hover:border-border'
              : 'border-transparent cursor-default'
        } ${mine ? 'text-brand-600' : 'text-txt-secondary'}`}
        title={`${gr.zones.map((z) => `${cityOf(z.tz)} (${z.count})`).join(', ') || cityOf(main.tz)}${mine ? ' — your time' : ''}${clickable ? ` — click to ${selected ? 'clear the filter' : 'filter leads to this timezone'}` : ''}`}
      >
        {/* Clock on the LEFT of everything */}
        <ClockFace minutesOfDay={minutesOfDay} highlight={mine || selected} />
        {/* Right column: place / time / lead count, one below the other */}
        <span className="flex flex-col">
          <span className="text-[11px] whitespace-nowrap">
            <span className="mr-1">{flagOf(main.tz)}</span>
            <span className="font-bold text-txt-primary">{abbrevOf(main.tz)}</span>
            {others > 0 && <span className="text-txt-tertiary"> +{others}</span>}
          </span>
          <span className="text-[16px] font-bold tabular-nums text-txt-primary">{timeFromOffset(main.tz, now)}</span>
          <span className={`text-[10px] whitespace-nowrap ${selected ? 'text-brand-700 font-semibold' : 'text-txt-tertiary'}`}>
            {gr.count} lead{gr.count === 1 ? '' : 's'}{selected ? ' · filtering' : mine ? ' · you' : ''}
          </span>
        </span>
      </button>
    )
  }

  // COLLAPSIBLE (Lee, 14 Oct 2026): folds to a slim bar to save vertical
  // space; open by default, the choice survives reloads (localStorage).
  if (collapsed) {
    return (
      <div
        className="shrink-0 border-b border-border bg-surface px-5 py-1 flex items-center gap-3 cursor-pointer hover:bg-surface-hover"
        onClick={() => setCollapsedPersist(false)}
        title="Click to expand the clock wall"
      >
        <span className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Clocks</span>
        <span className="text-[11px] font-semibold tabular-nums text-txt-secondary truncate">
          London {timeFromOffset('Europe/London', now)}
          {restGroups.slice(0, 6).map((gr) => {
            const main = [...gr.zones].sort((a, b) => b.count - a.count)[0]
            return ` · ${abbrevOf(main.tz)} ${timeFromOffset(main.tz, now)}`
          }).join('')}
          {restGroups.length > 6 ? ` · +${restGroups.length - 6}` : ''}
        </span>
        {tzFilter && <span className="text-[10px] font-bold text-brand-700">filtering: {tzFilter.label}</span>}
        <span className="ml-auto text-[12px] text-txt-tertiary shrink-0">expand ▾</span>
      </div>
    )
  }

  return (
    <div className="shrink-0 border-b border-border bg-surface px-5 py-2 flex items-center [--clock-face:theme(colors.white)] dark:[--clock-face:#1e293b]">
      {/* Static London reference, pinned left of the scroll */}
      <div className="shrink-0 pr-4 mr-4 border-r border-border">
        {renderClock(londonGroup || { off: londonOff, zones: [], count: 0 }, 'Europe/London')}
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-7 overflow-x-auto">
        {restGroups.map((gr) => renderClock(gr))}
      </div>
      <button
        onClick={() => setCollapsedPersist(true)}
        className="shrink-0 ml-3 self-start px-1.5 py-0.5 rounded text-[11px] font-bold text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover border border-transparent hover:border-border"
        title="Collapse the clock wall"
      >▴</button>
    </div>
  )
}
